/**
 * Cloudflare Turnstile token retrieval for the (legacy Vue 2) app.
 *
 * Replaces the invisible reCAPTCHA v3 flow. We load Cloudflare's script on
 * demand and render a transient hidden widget to obtain a token, keeping the
 * call sites (main.ts boot, Login.vue) a one-liner — no Vue plugin needed.
 *
 * Resolves to '' (and never throws) in development, when no site key is
 * configured, or on any failure. The backend only enforces the token in
 * production.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove: (id: string) => void
  reset: (id?: string) => void
}

function getTurnstile(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile
}

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (getTurnstile()) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('Failed to load Cloudflare Turnstile'))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

export async function getTurnstileToken(action = 'login'): Promise<string> {
  const siteKey = process.env.VUE_APP_TURNSTILE_SITEKEY
  if (process.env.NODE_ENV === 'development' || !siteKey) return ''

  try {
    await loadScript()
  } catch {
    return ''
  }

  return new Promise<string>(resolve => {
    const container = document.createElement('div')
    container.style.display = 'none'
    document.body.appendChild(container)

    let widgetId: string | undefined
    const cleanup = () => {
      try {
        if (widgetId) getTurnstile()?.remove(widgetId)
      } catch {
        /* ignore */
      }
      container.remove()
    }

    const timer = window.setTimeout(() => {
      cleanup()
      resolve('')
    }, 8000)

    try {
      widgetId = getTurnstile()!.render(container, {
        sitekey: siteKey,
        action,
        callback: (token: string) => {
          window.clearTimeout(timer)
          cleanup()
          resolve(token)
        },
        'error-callback': () => {
          window.clearTimeout(timer)
          cleanup()
          resolve('')
        },
      })
    } catch {
      window.clearTimeout(timer)
      cleanup()
      resolve('')
    }
  })
}

/**
 * Render a *visible* Turnstile widget into an in-DOM element using the
 * `interaction-only` appearance. Unlike getTurnstileToken() (which renders into
 * a hidden container and can never display a challenge), this stays invisible
 * and resolves a token silently on normal logins, and only draws the challenge
 * when Cloudflare actually requires manual interaction — so the user has
 * something to click. Use this on the interactive login form.
 *
 * onToken('') is called in dev / when unconfigured / on error or expiry, so the
 * caller can forward the value directly. Returns the widget id for reset.
 */
export async function renderTurnstileWidget(
  el: HTMLElement,
  onToken: (token: string) => void,
  onError?: () => void,
): Promise<string | undefined> {
  const siteKey = process.env.VUE_APP_TURNSTILE_SITEKEY
  if (process.env.NODE_ENV === 'development' || !siteKey) {
    onToken('')
    return undefined
  }

  try {
    await loadScript()
  } catch {
    onToken('')
    return undefined
  }

  try {
    return getTurnstile()!.render(el, {
      sitekey: siteKey,
      action: 'login',
      appearance: 'interaction-only',
      callback: (token: string) => onToken(token),
      'error-callback': () => {
        onToken('')
        onError?.()
      },
      'expired-callback': () => onToken(''),
    })
  } catch {
    onToken('')
    onError?.()
    return undefined
  }
}

/** Reset a rendered widget so it issues a fresh, single-use token. */
export function resetTurnstileWidget(widgetId?: string): void {
  try {
    getTurnstile()?.reset(widgetId)
  } catch {
    /* ignore */
  }
}
